import { Capacitor } from "@capacitor/core";
import { LEGAL } from "./legal";

/**
 * Single source for the API base.
 *
 * On the web the dev server and the API share a machine, so a localhost URL is
 * correct. In a packaged app "localhost" is the phone itself, so the same value
 * points at nothing and every request fails with a bare network error. Native
 * builds therefore read REACT_APP_NATIVE_BACKEND_URL, and if that is missing the
 * mistake is reported loudly at startup rather than showing up as an empty feed.
 */
const isNative = Capacitor.isNativePlatform();
const webBackend = process.env.REACT_APP_BACKEND_URL || "";
const nativeBackend = process.env.REACT_APP_NATIVE_BACKEND_URL || "";

const looksLocal = (url) => /^https?:\/\/(localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\])/i.test(url);

const resolveBackend = () => {
  if (!isNative) return webBackend;
  if (nativeBackend) return nativeBackend;
  if (looksLocal(webBackend)) {
    console.error(
      "[TrendingZ] REACT_APP_NATIVE_BACKEND_URL is not set and REACT_APP_BACKEND_URL " +
        `points at ${webBackend}, which on a device means the device itself. ` +
        "Set REACT_APP_NATIVE_BACKEND_URL to a host the phone can reach " +
        "(your machine's LAN IP for testing, or the public API URL) and rebuild."
    );
  }
  return webBackend;
};

export const BACKEND_URL = resolveBackend();
export const API = `${BACKEND_URL}/api`;
export const IS_NATIVE = isNative;

/**
 * YouTube embed URL, and the origin the player's messages arrive from.
 *
 * The iOS app is served from capacitor://localhost, which sends no Referer, and
 * YouTube refuses an embed without one: error 153, nothing plays. iOS therefore
 * loads the player through public/yt.html on the real domain, which embeds
 * YouTube and relays postMessage both ways. Android (https://localhost) and the
 * web already send a usable referrer, so they embed YouTube directly as before.
 */
const IS_IOS = Capacitor.getPlatform() === "ios";
export const ytEmbedUrl = (id, params) =>
  IS_IOS
    ? `${LEGAL.site}/yt.html?v=${id}&${params}`
    : `https://www.youtube.com/embed/${id}?${params}`;
export const YT_MESSAGE_ORIGIN = IS_IOS ? LEGAL.site : "https://www.youtube.com";

/**
 * Click-tracking redirect URL for a video.
 *
 * Every outbound link goes through /api/r/ so clicks are attributed. Shares use the
 * same URL, which is what makes shared links measurable.
 */
export const trackedVideoUrl = (video, countryCode) => {
  const params = new URLSearchParams({
    country: countryCode || "IN",
    type: video.platform || "youtube",
    category: video.category_id || "",
    title: video.title || "",
    channel: video.channel_title || "",
  });
  return `${API}/r/${video.id}?${params.toString()}`;
};

/**
 * Public site origin, for links that leave the app and have to work for someone
 * who has never installed it.
 *
 * Not window.location.origin: in a packaged build that is capacitor://localhost,
 * which resolves to nothing on the recipient's phone.
 */
export const PUBLIC_SITE = (process.env.REACT_APP_PUBLIC_SITE_URL || "https://trendingz.dessot.com").replace(/\/$/, "");

export const ANDROID_PACKAGE = "com.trendingz.app";
export const PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;

/**
 * The URL to hand to another person for a video.
 *
 * This deliberately does NOT use trackedVideoUrl. That one is the /api/r/
 * redirect, which 302s straight to youtube.com - so a shared link dropped the
 * recipient on YouTube and they never saw TrendingZ at all, which defeats the
 * point of sharing. This points at our own /video/:id page, which plays the video,
 * shows related ones, and is served real meta tags by the backend so the link
 * unfurls with a title and thumbnail.
 *
 * Click attribution survives: the recipient's "Watch on YouTube" button on that
 * page still goes through /api/r/, and time spent watching is counted by the
 * existing heartbeat.
 */
export const shareableVideoUrl = (video, countryCode) => {
  const params = new URLSearchParams({ country: countryCode || "IN", utm_source: "share" });
  return `${PUBLIC_SITE}/video/${video.id}?${params.toString()}`;
};

export const formatCount = (n) => {
  if (!n && n !== 0) return "";
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

export const timeAgo = (iso) => {
  if (!iso) return "";
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  const units = [
    ["year", 31536000],
    ["month", 2592000],
    ["week", 604800],
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
  ];
  for (const [name, secs] of units) {
    const value = Math.floor(seconds / secs);
    if (value >= 1) return `${value} ${name}${value > 1 ? "s" : ""} ago`;
  }
  return "just now";
};
